import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { deleteEstimate, getEstimateDetail } from '../api/estimate';
import EstimateForm from '../components/estimate/EstimateForm';
import Loading from '../components/common/Loading';
import PageTitle from '../components/layout/PageTitle';
import { ESTIMATE_TYPES } from '../const/estimateOptions';

/** 서버 응답을 견적 폼 입력값 형태로 변환한다. */
function toFormValues(detail) {
  // 지역 선택을 없애기 전에 저장된 값은 '[서울]강남역' 형태다. 앞의 지역 표기를 떼고 한 칸에 담는다.
  const stripRegion = (place = '') => String(place).replace(/^\[[^\]]+\]\s*/, '');
  const splitDateTime = (value = '') => {
    const [date = '', time = ''] = String(value).split('T');
    return { date, time: time.slice(0, 5) };
  };

  const depart = stripRegion(detail.departPlace);
  const arrival = stripRegion(detail.arrivalPlace);
  const departDateTime = splitDateTime(detail.departDate);
  const arrivalDateTime = splitDateTime(detail.arrivalDate);

  return {
    name: detail.name ?? '',
    phone: detail.phone ?? '',
    email: detail.email ?? '',
    password: detail.password ?? '',
    emailReceipt: Boolean(detail.emailReceipt),
    companyName: detail.companyName ?? '',
    travelType: detail.travelType || '일반여행',
    vehicleType: detail.vehicleType ?? '',
    vehicleNumber: String(detail.vehicleNumber ?? ''),
    memberCount: String(detail.memberCount ?? ''),
    boardingCount: detail.boardingCount ? String(detail.boardingCount) : '',
    departDate: departDateTime.date || undefined,
    departTime: departDateTime.time || '05:00',
    arrivalDate: arrivalDateTime.date || undefined,
    arrivalTime: arrivalDateTime.time || '05:00',
    commuteStartTime: detail.commuteStartTime || '07:00',
    commuteEndTime: detail.commuteEndTime || '18:00',
    departPlace: depart,
    arrivalPlace: arrival,
    stopCount: String(detail.stopCount ?? '0'),
    stopPlace: detail.stopPlace ?? '',
    workType: detail.workType ?? '',
    workDays: detail.workDays ?? '',
    wayType: detail.wayType || '왕복',
    payment: detail.payment || '현금',
    taxBill: detail.taxBill || '발급안함',
    memo: detail.memo ?? '',
  };
}

/** 내 견적 상세·수정. 등록 폼과 같은 컴포넌트를 사용해 입력 항목이 동일하다. */
export default function EstimateDetail() {
  const navigate = useNavigate();
  const { page, estimateId } = useParams();
  const { state } = useLocation();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDelete, setOpenDelete] = useState(false);

  useEffect(() => {
    document.title = '견적 상세 | 이케이하나관광';
  }, []);

  useEffect(() => {
    if (!state?.form) {
      navigate('/my', { replace: true });
      return;
    }

    setLoading(true);
    getEstimateDetail(state.form, estimateId)
      .then((response) => setDetail(response))
      .catch(() => setError('견적을 불러오지 못했습니다. 다시 조회해주세요.'))
      .finally(() => setLoading(false));
  }, [estimateId]);

  const handleDelete = () => {
    setOpenDelete(false);
    setLoading(true);
    deleteEstimate(estimateId, state.form)
      .then(() => navigate(`/my/list/${page || 1}`, { state: { form: state.form } }))
      .catch(() => setError('삭제에 실패했습니다. 잠시 후 다시 시도해주세요.'))
      .finally(() => setLoading(false));
  };

  const estimateType =
    detail?.estimateType === ESTIMATE_TYPES.COMMUTE ? ESTIMATE_TYPES.COMMUTE : ESTIMATE_TYPES.GENERAL;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 4 } }}>
      <PageTitle
        title='견적 상세 / 수정'
        subtitle={detail ? `견적번호 ${detail.id} · ${estimateType} 견적` : undefined}
      />

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {detail && (
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, backgroundColor: '#FFFACB' }}>
          <Typography sx={{ mb: 2, fontSize: 13, color: '#6b5544', wordBreak: 'keep-all' }}>
            요청일 {String(detail.createdDate ?? '').substring(0, 16)} · 내용을 고친 뒤 [수정 내용
            저장]을 눌러주세요. (본인확인 완료 상태)
          </Typography>

          <EstimateForm
            mode='modify'
            estimateId={estimateId}
            estimateType={estimateType}
            auth={state.form}
            initialValues={toFormValues(detail)}
            onUpdated={() =>
              navigate(`/my/list/${page || 1}`, { state: { form: state.form } })
            }
          />

          <Stack direction='row' spacing={1} sx={{ mt: 3, justifyContent: 'center' }}>
            <Button
              variant='outlined'
              onClick={() => navigate(`/my/list/${page || 1}`, { state: { form: state.form } })}
              sx={{ color: '#5A4231', borderColor: '#5A4231' }}
            >
              목록으로
            </Button>
            <Button variant='outlined' color='error' onClick={() => setOpenDelete(true)}>
              견적 삭제
            </Button>
          </Stack>
        </Paper>
      )}

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>견적을 삭제할까요?</DialogTitle>
        <DialogContent>삭제한 견적은 되돌릴 수 없습니다.</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>취소</Button>
          <Button color='error' onClick={handleDelete}>
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      <Loading open={loading} />
    </Box>
  );
}
